<template>
  <div
    class="oselect-wrapper"
    :class="{
      'disabled': disabled,
      'oselect-wrapper-error': hasError
    }"
  >
    <label
      v-if="label"
      class="o-caption block mb-1"
      :for="label"
      v-text="required ? `${label}*` : label"
    />
    <div v-if="hasError && !disabled" class="error pb-1 mt-0">
      <OSvg svg="danger" class="fill-red-300" width="14" />
      <span class="o-caption" v-text="error" />
    </div>
    <v-select
      class="select"
      :value="value"
      :options="options"
      :label="optionLabel"
      :placeholder="placeholder"
      :disabled="disabled"
      :required="required"
      :clearable="clearable"
      :searchable="searchable"
      :multiple="multiple"
      :close-on-select="closeOnSelect"
      :calculate-position="withPopper"
      :selectable="selectable"
      append-to-body
      @input="emitSelected"
    >
      <template #selected-option="option">
        <div class="flex items-center" :class="[textClasses, disabled ? 'opacity-25' : '']">
          <OSvg
            v-if="option.svg"
            :svg="option.svg"
            class="icon w-auto"
            :class="svgClasses"
          />
          <span :class="option.svg ? 'pl-2' : ''">
            {{ option.label }}
          </span>
        </div>
      </template>
      <template #option="option">
        <div class="flex items-center py-2" :class="textClasses">
          <OSvg
            v-if="option.svg"
            :svg="option.svg"
            class="icon w-auto"
            :class="svgClasses"
          />
          <span class="ota-body-light" :class="option.svg ? 'pl-2' : ''">
            {{ option.label }}
          </span>
        </div>
      </template>
      <template #open-indicator>
        <OSvg
          v-if="!disabled"
          class="w-4 fill-current text-black"
          :svg="svg || 'arrow-down'"
          :class="svgClasses"
        />
        <span v-else />
      </template>
    </v-select>
  </div>
</template>

<script>
import 'vue-select/dist/vue-select.css'
import { createPopper } from '@popperjs/core'
import VSelect from 'vue-select'
const OSvg = () => import('@/components/OSvg/OSvg')

export default {
  name: 'OSelect',
  components: { OSvg, VSelect },
  props: {
    label: {
      type: String,
      default: '',
      required: false
    },
    value: {
      type: [String, Object, Number],
      default: null
    },
    options: {
      type: Array,
      required: true
    },
    optionLabel: {
      type: String,
      default: 'label'
    },
    svg: {
      type: String,
      default: ''
    },
    svgClasses: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Select'
    },
    selectable: {
      type: Function,
      default: () => true
    },
    error: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    searchable: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    selectClasses: {
      type: Array,
      default: () => []
    },
    placement: {
      type: String,
      default: 'bottom'
    },
    textClasses: {
      type: String,
      default: ''
    }
  },
  computed: {
    hasError () {
      return !!this.error
    }
  },
  methods: {
    emitSelected (value) {
      this.$emit('input', value)
    },
    withPopper (dropdownList, component, { width }) {
      /**
       * We need to explicitly define the dropdown width since
       * it is usually inherited from the parent with CSS.
       */
      dropdownList.style.width = width

      /**
       * Here we position the dropdownList relative to the $refs.toggle Element.
       *
       * The 'offset' modifier aligns the dropdown so that the $refs.toggle and
       * the dropdownList overlap by 1 pixel.
       *
       * The 'toggleClass' modifier adds a 'drop-up' class to the Vue Select
       * wrapper so that we can set some styles for when the dropdown is placed
       * above.
       */
      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -1]
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn ({ state }) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            }
          }]
      })

      /**
       * To prevent memory leaks Popper needs to be destroyed.
       * If you return function, it will be called just before dropdown is removed from DOM.
       */
      return () => popper.destroy()
    }
  }
}
</script>

<style lang="postcss">
  .oselect-wrapper {
    &-error {
      .vs__dropdown-toggle {
        @apply border-red-300 !important;
      }
    }

    .v-select {
      .vs__ {
        &dropdown-toggle {
          @apply bg-white relative border border-gray-600 rounded text-md leading-none;
          padding: .5rem .4rem;

          &::placeholder {
            @apply text-gray-400;
          }

          input {
            @apply bg-transparent;
          }
        }

        &dropdown-menu {
          @apply my-3 rounded shadow;
        }

        &selected {
          margin: 0 .2rem 0;

          &-options {
            flex-wrap: unset;
          }
        }
      }
    }

    &.disabled {
      .v-select {
        .vs__ {
          &dropdown-toggle {
            @apply bg-gray-200 border-gray-200;
          }
        }
      }
    }
  }
</style>

<style lang="postcss" scoped>
  .oselect-wrapper {

    .label {
      @apply block font-body text-sm leading-tight mb-2;
    }

    .icon {
      @apply relative;
    }

    .error {
      @apply flex items-center text-red-300;

      svg {
        @apply flex mr-1
      }
    }
  }
</style>
